import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Polls /api/payments/status/{session_id} up to 10 times (2s apart, 20s total).
 * On paid: fires success UI and shows "Continue" to the appropriate route.
 */
const PaymentSuccess = () => {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const sessionId = sp.get('session_id');
  const [state, setState] = useState({ phase: 'polling', txn: null, error: null });
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ phase: 'error', error: 'Missing session id' }); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      pollsRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') { setState({ phase: 'paid', txn: data }); return; }
        if (['failed', 'expired'].includes(data.payment_status)) { setState({ phase: 'failed', txn: data }); return; }
        if (pollsRef.current >= 10) { setState({ phase: 'timeout', txn: data }); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ phase: 'error', error: e?.response?.data?.detail || 'Could not fetch payment status' });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const continueTo = (() => {
    const purpose = state.txn?.purpose;
    if (purpose === 'wallet_topup') return { label: 'Back to wallet', path: '/wallet' };
    if (purpose === 'subscription') return { label: 'View my subscription', path: '/profile' };
    if (purpose === 'booking') return { label: 'View my bookings', path: '/bookings' };
    return { label: 'Back to home', path: '/home' };
  })();

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-5">
      <div data-testid="payment-success-card" className="bg-white border border-neutral-200 rounded-2xl p-8 max-w-md w-full text-center shadow-sm">
        {state.phase === 'polling' && (
          <>
            <Loader2 className="w-12 h-12 text-[#2E7D32] mx-auto animate-spin" />
            <h1 className="mt-4 text-xl font-extrabold text-neutral-900">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-neutral-500">This usually takes a few seconds. Please don't close this window.</p>
          </>
        )}
        {state.phase === 'paid' && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#2E7D32] mx-auto" />
            <h1 className="mt-4 text-2xl font-extrabold text-neutral-900" data-testid="payment-success-title">Payment successful!</h1>
            <p className="mt-2 text-sm text-neutral-500">
              ₹{Number(state.txn?.amount || 0).toLocaleString('en-IN')} paid via Stripe.
              {state.txn?.purpose === 'wallet_topup' && ' Your wallet has been credited.'}
              {state.txn?.purpose === 'subscription' && ' Your subscription is now active.'}
              {state.txn?.purpose === 'booking' && ' Your booking is now marked paid.'}
            </p>
            <button data-testid="payment-success-continue-btn" onClick={() => nav(continueTo.path)} className="mt-6 w-full inline-flex items-center justify-center gap-1.5 bg-[#2E7D32] hover:bg-[#276D2A] text-white font-bold py-3 rounded-xl">
              {continueTo.label} <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {state.phase === 'failed' && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto" />
            <h1 className="mt-4 text-xl font-extrabold text-neutral-900">Payment did not complete</h1>
            <p className="mt-2 text-sm text-neutral-500">Your session {state.txn?.payment_status}. Please try again from the previous page.</p>
            <button onClick={() => nav('/home')} className="mt-6 w-full py-3 rounded-xl bg-white border border-neutral-200 hover:border-[#2E7D32] font-bold">Back to home</button>
          </>
        )}
        {state.phase === 'timeout' && (
          <>
            <Loader2 className="w-12 h-12 text-neutral-400 mx-auto" />
            <h1 className="mt-4 text-xl font-extrabold text-neutral-900">Still processing…</h1>
            <p className="mt-2 text-sm text-neutral-500">Stripe is taking longer than usual. Check your wallet/bookings shortly — you'll see it credited once confirmed.</p>
            <button onClick={() => nav(continueTo.path)} className="mt-6 w-full py-3 rounded-xl bg-[#2E7D32] hover:bg-[#276D2A] text-white font-bold">{continueTo.label}</button>
          </>
        )}
        {state.phase === 'error' && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto" />
            <h1 className="mt-4 text-xl font-extrabold text-neutral-900">Something went wrong</h1>
            <p className="mt-2 text-sm text-neutral-500">{state.error}</p>
            <button onClick={() => nav('/home')} className="mt-6 w-full py-3 rounded-xl bg-white border border-neutral-200 font-bold">Back to home</button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
